import { useCallback, useEffect, useState } from 'react'
import { useLazyQuery } from '@apollo/client'
import moment, { Moment } from 'moment-timezone'
import { useDeepCompareMemo } from 'use-deep-compare'
import { GET_ALL_SLOTS_CALENDAR_PARENT, GET_ALL_SLOTS_CALENDAR_STAFF } from '@/modules/API/other/calendar'
import { GET_RATE_SLOTS } from '@/modules/API/staff/staffRate'
import { GET_ALL_REQUESTS } from '@/modules/API/daycare/daycareHandler'
import { AllSlots, EventCalendar } from '@/types/Calendar'
import { DateRateSlot } from '@/types/StaffRate'
import { dataToCalendarEvents, getDateClosingDays, rateSlotsDataToCalendarEvents } from '../utils/calendarParameters'
import { PlanningRights } from '@/hooks/usePlanningRights'
import { Daycare } from '@/types/Daycare'
import { Role } from '@/types/Role'

const EMPTY_SLOTS: AllSlots = {
    getSlots: {
        OccasionalSlots: [],
        RegularSlots: [],
        RequestSlots: []
    },
    getWorks: [],
    getClosingDay: [],
    getEventDay: []
}

interface UsePlanningSlotsParams {
    rights: PlanningRights
    isStaff: boolean
    currentDaycare: Daycare | undefined
    role: Role | undefined
}

export interface FetchPlanningDataProps {
    fromDate: Moment
    toDate: Moment
    isGlobalPlanning: boolean
}

interface UsePlanningSlotsReturn {
    slots: AllSlots
    rateSlots: DateRateSlot[]
    calendarEvents: EventCalendar[]
    requestNumber: number
    lastFromDate: Moment
    lastToDate: Moment
    isLoading: boolean
    fetchData: (props: FetchPlanningDataProps) => void
    refetchData: (isGlobalPlanning?: boolean) => void
}

export function usePlanningSlots({
    rights,
    isStaff,
    currentDaycare,
    role
}: UsePlanningSlotsParams): UsePlanningSlotsReturn {
    // SLOTS STATE
    const [slots, setSlots] = useState<AllSlots>(EMPTY_SLOTS)
    const [rateSlots, setRateSlots] = useState<DateRateSlot[]>([])
    const [requestNumber, setRequestNumber] = useState<number>(0)
    const [lastFromDate, setLastFromDate] = useState<Moment>(moment())
    const [lastToDate, setLastToDate] = useState<Moment>(moment())
    const [isLoading, setIsLoading] = useState(false)

    // QUERIES
    const [getAllSlotsParent] = useLazyQuery(GET_ALL_SLOTS_CALENDAR_PARENT)
    const [getAllSlotsStaff] = useLazyQuery(GET_ALL_SLOTS_CALENDAR_STAFF)
    const [getRateSlotsQuery] = useLazyQuery(GET_RATE_SLOTS)
    const [getRequestNumber, { data: dataRequestNumber, error: errorRequestNumber }] = useLazyQuery(GET_ALL_REQUESTS)

    useEffect(() => {
        setRequestNumber(errorRequestNumber ? 0 : dataRequestNumber?.getAllRequests || 0)
    }, [dataRequestNumber, errorRequestNumber])

    // CALENDAR EVENTS MEMOIZATION
    const rateSlotsCalendarEvents: EventCalendar[] = useDeepCompareMemo((): EventCalendar[] => {
        if (!currentDaycare) return []
        return rateSlotsDataToCalendarEvents(rateSlots)
    }, [rateSlots, currentDaycare])

    const allSlotsCalendarEvents: EventCalendar[] = useDeepCompareMemo((): EventCalendar[] => {
        if (!currentDaycare || !role) return []
        const datesClosingDays: Moment[] = getDateClosingDays(slots.getClosingDay)
        return dataToCalendarEvents(slots, datesClosingDays, currentDaycare, role)
    }, [slots, currentDaycare, role])

    const calendarEvents: EventCalendar[] = useDeepCompareMemo((): EventCalendar[] => {
        if (!currentDaycare) return []
        return [...rateSlotsCalendarEvents, ...allSlotsCalendarEvents]
    }, [rateSlotsCalendarEvents, allSlotsCalendarEvents, currentDaycare])

    // FETCH FUNCTIONS
    const fetchData: (props: FetchPlanningDataProps) => void = useCallback(
        ({ fromDate, toDate, isGlobalPlanning }: FetchPlanningDataProps) => {
            if (!currentDaycare?.id) return

            setIsLoading(true)
            setSlots(EMPTY_SLOTS)
            setRateSlots([])
            setLastFromDate(fromDate)
            setLastToDate(toDate)

            const dateVariables: { from: string; to: string } = {
                from: fromDate.locale('fr').format('DD-MM-YYYY'),
                to: toDate.locale('fr').format('DD-MM-YYYY')
            }

            if (rights.isParent) {
                getAllSlotsParent({
                    variables: {
                        daycare_id: currentDaycare.id,
                        ...dateVariables
                    }
                })
                    .then(({ data }): void => {
                        if (data) setSlots(data)
                    })
                    .finally(() => setIsLoading(false))
            } else {
                if (rights.canReadContracts || rights.canReadOccasionalSlot) {
                    getRequestNumber({ variables: { daycareId: currentDaycare.id } })
                }

                if (isGlobalPlanning) {
                    getRateSlotsQuery({
                        variables: {
                            daycareId: currentDaycare.id,
                            ...dateVariables
                        }
                    })
                        .then(({ data }): void => {
                            if (data?.getRateSlots) setRateSlots(data.getRateSlots)
                        })
                        .finally(() => setIsLoading(false))
                } else {
                    getAllSlotsStaff({
                        variables: {
                            daycare_id: currentDaycare.id,
                            ...dateVariables,
                            retrieveWorks: isStaff
                        }
                    })
                        .then(({ data }): void => {
                            if (data) setSlots(data)
                        })
                        .finally(() => setIsLoading(false))
                }
            }
        },
        [currentDaycare, rights, isStaff, getAllSlotsParent, getAllSlotsStaff, getRateSlotsQuery, getRequestNumber]
    )

    const refetchData: (isGlobalPlanning?: boolean) => void = useCallback(
        (isGlobalPlanning?: boolean) => {
            fetchData({ fromDate: lastFromDate, toDate: lastToDate, isGlobalPlanning: isGlobalPlanning ?? false })
        },
        [lastFromDate, lastToDate, fetchData]
    )

    return {
        slots,
        rateSlots,
        calendarEvents,
        requestNumber,
        lastFromDate,
        lastToDate,
        isLoading,
        fetchData,
        refetchData
    }
}
